import moment from 'moment';
import { create } from 'zustand';

import { apiService } from '@/services/api-service';

import { mapSortIdToField } from '@/utils/search-utils';

import { USER_ROLES } from '@/constants/global-constants';
import { SEARCH_PARAMS_FILES } from '@/constants/search-params';

import { useApiManagerStore } from './apiManagerStore';
import { useAuthStore } from './authStore';
import { useFileExplorerStore } from './fileExplorerStore';
import { useFiltersStore } from './filtersStore';
import { useGlobalStore } from './globalStore';

// Helpers to build ES clauses compatible with indices that may or may not
// have multi-fields like ".enum". Each helper returns a bool/should that
// matches either the base field or its ".enum" subfield.
const termEither = (fieldBase: string, value: string) => ({
  bool: {
    should: [
      { term: { [fieldBase]: value } },
      { term: { [`${fieldBase}.enum`]: value } },
    ],
    minimum_should_match: 1,
  },
});

const termsEither = (fieldBase: string, values: string[]) => ({
  bool: {
    should: [
      { terms: { [fieldBase]: values } },
      { terms: { [`${fieldBase}.enum`]: values } },
    ],
    minimum_should_match: 1,
  },
});

const wildcardEither = (fieldBase: string, value: string, boost?: number) => ({
  bool: {
    should: [
      {
        wildcard: {
          [fieldBase]: {
            value: `*${value}*`,
            case_insensitive: true,
            ...(boost ? { boost } : {}),
          },
        },
      },
      {
        wildcard: {
          [`${fieldBase}.enum`]: {
            value: `*${value}*`,
            case_insensitive: true,
            ...(boost ? { boost } : {}),
          },
        },
      },
    ],
    minimum_should_match: 1,
  },
});

const existsEntity = (anchorField: string) => ({
  exists: { field: anchorField },
});

interface DateRange {
  from: string | null;
  to: string | null;
}

interface SearchStore {
  searchPageResults: string[];
  searchPageTerm: string;
  setSearchPageResults: (results: string[]) => void;
  setSearchPageTerm: (term: string) => void;
  searchAgreementsUuids: (params: {
    field: string;
    value: string;
    id: string;
  }) => Promise<string[]>;
  searchAgreementsForExport: () => Promise<string[]>;
  searchAgreementUuidsForMyDocuments: (
    currentPage: 'search' | 'dashboard',
    ignorePagination?: boolean,
  ) => Promise<string[]>;
  searchAgreementFields: (
    currentPage: 'search' | 'dashboard',
    field: string,
  ) => Promise<string[]>;
  getDirectoriesByInsuranceObjects: (
    insuranceObjects: string[],
  ) => Promise<{ uuid: string; directory: string }[]>;
  getAgreementsByMask: (
    mask: string,
  ) => Promise<{ label: string; value: string }[]>;
  searchUsersByMask: (
    mask: string,
  ) => Promise<{ label: string; value: string }[]>;
}

export const useSearchStore = create<SearchStore>((set, get) => ({
  searchPageResults: [],
  searchPageTerm: '',
  setSearchPageResults: (results: string[]) => {
    set({ searchPageResults: results });
  },
  setSearchPageTerm: (term: string) => {
    set({ searchPageTerm: term });
  },
  searchAgreementsForExport: async () => {
    const { searchAgreementUuidsForMyDocuments } = get();
    const { visibleColumns } = useFileExplorerStore.getState();

    const agreementsUuids = await searchAgreementUuidsForMyDocuments(
      'dashboard',
      true,
    );

    const payload = {
      query: {
        bool: {
          must: [
            existsEntity('public_agreements_uuid'),
            termsEither('public_agreements_uuid', agreementsUuids),
          ],
        },
      },
      size: 10000,
      _source: [visibleColumns.map((column) => mapSortIdToField(column))],
      timeout: '30s',
    };

    const elasticSearch = await apiService.elasticSearch(payload);

    const elasticResults = elasticSearch.hits.hits.map(
      (hit) => hit._source.public_agreements_uuid,
    );

    return elasticResults;
  },

  searchAgreementsUuids: async ({ value, id }) => {
    const { user } = useAuthStore.getState();

    switch (id) {
      case SEARCH_PARAMS_FILES.DIRECTORY.id:
        const directories = useApiManagerStore.getState().directories;

        const directoryUuids = directories
          .filter(
            (i) =>
              i.name.toLowerCase().includes(value.trim().toLowerCase()) ||
              i.abbreviation.toLowerCase().includes(value.trim().toLowerCase()),
          )
          .map((i) => i.uuid);

        if (directoryUuids.length === 0) {
          return [];
        }

        try {
          let must: any[] = [
            existsEntity('public_insurance_objects_directory_id'),
          ];

          must.push(
            termsEither(
              'public_insurance_objects_directory_id',
              directoryUuids,
            ),
          );

          const payloadIO = {
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_insurance_objects_uuid'],
            timeout: '30s',
          } as const;
          const elasticSearchIO = await apiService.elasticSearch(payloadIO);

          const insuranceObjectUuids = [
            ...new Set(
              elasticSearchIO.hits.hits.map(
                (i) => i._source.public_insurance_objects_uuid,
              ),
            ),
          ];

          must = [existsEntity('public_agreements_insurance_object_id')];
          must.push(
            termsEither(
              'public_agreements_insurance_object_id',
              insuranceObjectUuids,
            ),
          );
          const payloadA = {
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_agreements_uuid'],
            timeout: '30s',
          } as const;

          const elasticSearch = await apiService.elasticSearch(payloadA);

          const agreementsUuids = [
            ...new Set(
              elasticSearch.hits.hits.map(
                (i) => i._source.public_agreements_uuid,
              ),
            ),
          ];

          return agreementsUuids;
        } catch (e) {
          console.error(
            'Elasticsearch query failed for DOCUMENT_NUMBER field:',
            e,
          );
          return [];
        }

      case SEARCH_PARAMS_FILES.DOCUMENT_NUMBER.id: {
        // Normalize by removing hyphens/spaces to handle formatted inputs like 25-33-21005
        const raw = value.trim();

        try {
          const must: any[] = [existsEntity('public_agreements_uuid')];

          // Always allow simple partial match of the raw input
          if (raw.length > 0) {
            must.push(wildcardEither('public_agreements_name', raw));
          }

          if (user?.role === USER_ROLES.EXECUTOR.value) {
            must.push({
              bool: {
                should: [
                  termEither('public_agreements_creator_id', user.uuid),
                  termEither('public_agreements_assignee_id', user.uuid),
                ],
                minimum_should_match: 1,
              },
            });
          }

          const payload = {
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_agreements_uuid'],
            timeout: '30s',
          } as const;
          const elasticSearch = await apiService.elasticSearch(payload);

          return [
            ...new Set(
              elasticSearch.hits.hits.map(
                (i) => i._source.public_agreements_uuid,
              ),
            ),
          ];
        } catch (e) {
          console.error(
            'Elasticsearch query failed for DOCUMENT_NUMBER field:',
            e,
          );
          return [];
        }
      }

      case SEARCH_PARAMS_FILES.UPLOAD_DATE.id:
        try {
          const dateRange = JSON.parse(value) as DateRange;

          const must: any[] = [
            existsEntity('public_files_uuid'),
            existsEntity('public_files_agreement_id'),
            {
              range: {
                public_files_upload_date: {
                  gte: dateRange.from,
                  lte: dateRange.to,
                },
              },
            },
          ];

          const elasticSearch = await apiService.elasticSearch({
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_files_agreement_id'],
            timeout: '30s',
          });

          const elasticResults = elasticSearch.hits.hits.map(
            (hit) => hit._source.public_files_agreement_id,
          );

          // If user is EXECUTOR, further filter agreements to those created by or assigned to the user
          if (user?.role === USER_ROLES.EXECUTOR.value) {
            if (elasticResults.length === 0) {
              return [];
            }

            const must: any[] = [
              existsEntity('public_agreements_uuid'),
              termsEither('public_agreements_uuid', elasticResults),
              {
                bool: {
                  should: [
                    termEither('public_agreements_creator_id', user.uuid),
                    termEither('public_agreements_assignee_id', user.uuid),
                  ],
                  minimum_should_match: 1,
                },
              },
            ];

            const agreementsFilterQuery = {
              query: {
                bool: {
                  must,
                },
              },
              size: 10000,
              _source: ['public_agreements_uuid'],
              timeout: '30s',
            };

            const agreementsFilterSearch = await apiService.elasticSearch(
              agreementsFilterQuery,
            );

            const filteredAgreementUuids = agreementsFilterSearch.hits.hits.map(
              (hit) => hit._source.public_agreements_uuid,
            );

            return Array.from(new Set([...filteredAgreementUuids]));
          }

          // Non-executor: Combine and deduplicate results from tag-agreement junction
          return Array.from(new Set([...elasticResults]));
        } catch (e) {
          console.error('Elasticsearch query failed for UPLOAD_DATE field:', e);
          // If parsing fails, use the old behavior
          return [];
        }

      case SEARCH_PARAMS_FILES.FILMED_DATE.id:
        try {
          const dateRange = JSON.parse(value) as DateRange;

          const elasticSearch = await apiService.elasticSearch({
            query: {
              bool: {
                must: [
                  existsEntity('public_files_uuid'),
                  {
                    range: {
                      public_files_filmed_date: {
                        gte: dateRange.from,
                        lte: dateRange.to,
                      },
                    },
                  },
                ],
              },
            },
            size: 10000,
            _source: ['public_files_agreement_id'],
            timeout: '30s',
          });

          const elasticResults = elasticSearch.hits.hits.map(
            (hit) => hit._source.public_files_agreement_id,
          );

          // If user is EXECUTOR, further filter agreements to those created by or assigned to the user
          if (user?.role === USER_ROLES.EXECUTOR.value) {
            if (elasticResults.length === 0) {
              return [];
            }

            const must: any[] = [
              existsEntity('public_agreements_uuid'),
              termsEither('public_agreements_uuid', elasticResults),
              {
                bool: {
                  should: [
                    termEither('public_agreements_creator_id', user.uuid),
                    termEither('public_agreements_assignee_id', user.uuid),
                  ],
                  minimum_should_match: 1,
                },
              },
            ];

            const agreementsFilterQuery = {
              query: {
                bool: {
                  must,
                },
              },
              size: 10000,
              _source: ['public_agreements_uuid'],
              timeout: '30s',
            };

            const agreementsFilterSearch = await apiService.elasticSearch(
              agreementsFilterQuery,
            );

            const filteredAgreementUuids = agreementsFilterSearch.hits.hits.map(
              (hit) => hit._source.public_agreements_uuid,
            );

            return Array.from(new Set([...filteredAgreementUuids]));
          }

          // Non-executor: Combine and deduplicate results from tag-agreement junction
          return Array.from(new Set([...elasticResults]));
        } catch (e) {
          console.error('Elasticsearch query failed for FILMED_DATE field:', e);
          // If parsing fails, use the old behavior
          return [];
        }

      case SEARCH_PARAMS_FILES.VIN_NUMBER.id:
      case SEARCH_PARAMS_FILES.EDRPOU_RNOKPP.id:
        if (!value || value.trim() === '') {
          return [];
        }

        try {
          // Step 1: Find matching insurance objects by name/VIN
          const insuranceObjectsQuery = {
            query: {
              bool: {
                must: [
                  {
                    term: {
                      table: 'insurance_objects',
                    },
                  },
                  {
                    wildcard: {
                      'public_insurance_objects_name.enum': {
                        value: `*${value.trim()}*`,
                        case_insensitive: true,
                      },
                    },
                  },
                ],
              },
            },
            size: 1000,
            _source: [
              'public_insurance_objects_uuid',
              'public_insurance_objects_name',
            ],
            timeout: '30s',
          };

          const insuranceObjectsResponse = await apiService.elasticSearch(
            insuranceObjectsQuery,
          );

          const insuranceObjectUuids = insuranceObjectsResponse.hits.hits.map(
            (hit) => hit._source.public_insurance_objects_uuid,
          );

          // Early return if no matching insurance objects found
          if (insuranceObjectUuids.length === 0) {
            const insuranceObjects = await apiService.getInsuranceObjects(
              undefined,
              {
                take: 30,
                skip: 0,
              },
            );
            const findMatchingInsuranceObjects = insuranceObjects.filter((i) =>
              i.name.toLowerCase().includes(value.trim().toLowerCase()),
            );
            if (findMatchingInsuranceObjects.length === 0) {
              return [];
            }
            const agreementUuids = findMatchingInsuranceObjects
              .map((i) => i.agreements)
              .flat();
            return agreementUuids;
          }

          // Step 2: Find agreements linked to these insurance objects
          // Debug logging to understand the terms query issue

          // Always use bool/should with match queries for consistency
          // This avoids the terms query issue entirely
          const agreementQuery = {
            bool: {
              should: insuranceObjectUuids.map((objectId) => ({
                ...termEither(
                  'public_agreements_insurance_object_id',
                  objectId,
                ),
              })),
              minimum_should_match: 1,
            },
          };
          const must: any[] = [
            existsEntity('public_agreements_uuid'),
            agreementQuery,
          ];

          if (user?.role === USER_ROLES.EXECUTOR.value) {
            must.push({
              bool: {
                should: [
                  termEither('public_agreements_creator_id', user.uuid),
                  termEither('public_agreements_assignee_id', user.uuid),
                ],
                minimum_should_match: 1,
              },
            });
          }

          const agreementsQuery = {
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_agreements_uuid'],
            timeout: '30s',
          };

          const agreementsResponse =
            await apiService.elasticSearch(agreementsQuery);

          const elasticResults = agreementsResponse.hits.hits.map(
            (hit) => hit._source.public_agreements_uuid,
          );

          // Combine and deduplicate results
          return Array.from(new Set([...elasticResults]));
        } catch (error) {
          console.error('Elasticsearch query failed for EDRPOU field:', error);

          // Check if it's a specific ES error we can handle
          if (error instanceof Error && error.message.includes('timeout')) {
            console.warn('Elasticsearch query timed out for EDRPOU search');
          }

          // Return empty array instead of falling back to API call
          // This maintains performance and avoids blocking the UI
          return [];
        }

      case SEARCH_PARAMS_FILES.STATE_LICENSE_PLATE.id:
        // If agreements array is empty, perform Elasticsearch search
        const must: any[] = [
          existsEntity('public_agreements_uuid'),
          wildcardEither('public_agreements_registration_plate', value.trim()),
        ];

        if (user?.role === USER_ROLES.EXECUTOR.value) {
          must.push({
            bool: {
              should: [
                termEither('public_agreements_creator_id', user.uuid),
                termEither('public_agreements_assignee_id', user.uuid),
              ],
              minimum_should_match: 1,
            },
          });
        }

        try {
          const elasticSearch = await apiService.elasticSearch({
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_agreements_uuid'],
            timeout: '30s',
          });

          return [
            ...elasticSearch.hits.hits.map(
              (i) => i._source.public_agreements_uuid,
            ),
          ];
        } catch (e) {
          console.error(
            'Elasticsearch query failed for STATE_LICENSE_PLATE field:',
            e,
          );
          return [];
        }

      case SEARCH_PARAMS_FILES.STATUSES.id:
        const steps = useGlobalStore.getState().steps;

        // Handle empty search value
        if (!value || value.trim() === '') {
          return [];
        }

        // Find matching step UUIDs by status name (from state)
        const matchingStepUuids = steps
          .filter((step) =>
            step.status_name.toLowerCase().includes(value.trim().toLowerCase()),
          )
          .map((step) => step.uuid);

        // Early return if no matching steps found
        if (matchingStepUuids.length === 0) {
          return [];
        }

        try {
          // Always use bool/should with match queries for consistency
          // This avoids the terms query issue entirely
          const stepQuery = {
            bool: {
              should: matchingStepUuids.map((stepId) => ({
                term: {
                  public_agreements_step_id: stepId,
                },
              })),
              minimum_should_match: 1,
            },
          };

          const must: any[] = [
            {
              term: {
                table: 'agreements',
              },
            },
            stepQuery,
          ];

          if (user?.role === USER_ROLES.EXECUTOR.value) {
            must.push({
              bool: {
                should: [
                  termEither('public_agreements_creator_id', user.uuid),
                  termEither('public_agreements_assignee_id', user.uuid),
                ],
                minimum_should_match: 1,
              },
            });
          }

          const statusQuery = {
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_agreements_uuid'],
            timeout: '30s',
          };

          const elasticSearch = await apiService.elasticSearch(statusQuery);

          const elasticResults = elasticSearch.hits.hits.map(
            (hit) => hit._source.public_agreements_uuid,
          );

          // Combine and deduplicate results
          return Array.from(new Set([...elasticResults]));
        } catch (error) {
          console.error(
            'Elasticsearch query failed for STATUSES field:',
            error,
          );

          // Check if it's a specific ES error we can handle
          if (error instanceof Error && error.message.includes('timeout')) {
            console.warn(
              'Elasticsearch query timed out, returning static results only',
            );
          }

          // Fallback to original implementation
          const agreementsByStatuses = useApiManagerStore.getState().agreements;
          const stepsMap = new Map(steps.map((i) => [i.uuid, i.status_name]));
          return agreementsByStatuses.reduce((acc, i) => {
            if (
              stepsMap
                .get(i.step)
                ?.toLowerCase()
                .includes(value.trim().toLowerCase())
            ) {
              acc.push(i.uuid);
            }
            return acc;
          }, [] as string[]);
        }

      case SEARCH_PARAMS_FILES.TAGS.id:
        const tags = useGlobalStore.getState().allTags.entities;

        // Handle empty search value
        if (!value || value.trim() === '') {
          return [];
        }

        const matchingTagUuids = Object.entries(tags)
          .filter(([_, tag]) =>
            tag.name.toLowerCase().includes(value.trim().toLowerCase()),
          )
          .map(([uuid, _]) => uuid);

        // Early return if no matching tags found
        if (matchingTagUuids.length === 0) {
          return [];
        }

        try {
          // Query the junction table to find agreements linked to matching tags
          const tagRelationshipQuery = {
            query: {
              bool: {
                must: [
                  existsEntity('public_tags_agreements_agreement_id'),
                  termsEither(
                    'public_tags_agreements_tag_id',
                    matchingTagUuids,
                  ),
                ],
              },
            },
            size: 10000,
            _source: ['public_tags_agreements_agreement_id'],
            timeout: '30s',
          };

          const elasticSearch =
            await apiService.elasticSearch(tagRelationshipQuery);

          const elasticResults = elasticSearch.hits.hits.map(
            (hit) => hit._source.public_tags_agreements_agreement_id,
          );

          // If user is EXECUTOR, further filter agreements to those created by or assigned to the user
          if (user?.role === USER_ROLES.EXECUTOR.value) {
            if (elasticResults.length === 0) {
              return [];
            }

            const must: any[] = [
              existsEntity('public_agreements_uuid'),
              termsEither('public_agreements_uuid', elasticResults),
              {
                bool: {
                  should: [
                    termEither('public_agreements_creator_id', user.uuid),
                    termEither('public_agreements_assignee_id', user.uuid),
                  ],
                  minimum_should_match: 1,
                },
              },
            ];

            const agreementsFilterQuery = {
              query: {
                bool: {
                  must,
                },
              },
              size: 10000,
              _source: ['public_agreements_uuid'],
              timeout: '30s',
            };

            const agreementsFilterSearch = await apiService.elasticSearch(
              agreementsFilterQuery,
            );

            const filteredAgreementUuids = agreementsFilterSearch.hits.hits.map(
              (hit) => hit._source.public_agreements_uuid,
            );

            return Array.from(new Set([...filteredAgreementUuids]));
          }

          // Non-executor: Combine and deduplicate results from tag-agreement junction
          return Array.from(new Set([...elasticResults]));
        } catch (error) {
          console.error('Elasticsearch query failed for TAGS field:', error);
          return [];
        }

      case SEARCH_PARAMS_FILES.INDEX.id:
        const indexes = useGlobalStore.getState().indexes;

        // Handle empty search value
        if (!value || value.trim() === '') {
          return [];
        }

        const indexUuidsWithSearchValue = indexes.filter(
          (i) =>
            i.name.toLowerCase().includes(value.trim().toLowerCase()) &&
            !i.is_deleted,
        );

        const indexUuids = indexUuidsWithSearchValue.map((i) => i.uuid);

        // Early return if no matching indexes found
        if (indexUuids.length === 0) {
          return [];
        }

        try {
          const must: any[] = [
            existsEntity('public_agreements_uuid'),
            termsEither('public_agreements_index_id', indexUuids),
          ];

          if (user?.role === USER_ROLES.EXECUTOR.value) {
            must.push({
              bool: {
                should: [
                  termEither('public_agreements_creator_id', user.uuid),
                  termEither('public_agreements_assignee_id', user.uuid),
                ],
                minimum_should_match: 1,
              },
            });
          }

          const queryPayload = {
            query: {
              bool: {
                must,
              },
            },
            size: 10000,
            _source: ['public_agreements_uuid'],
            timeout: '30s',
          };

          const elasticSearch = await apiService.elasticSearch(queryPayload);

          const elasticResults = elasticSearch.hits.hits.map(
            (hit) => hit._source.public_agreements_uuid,
          );

          // Combine and deduplicate results
          return Array.from(new Set([...elasticResults]));
        } catch (error) {
          // Better error handling with logging
          console.error('Elasticsearch query failed for INDEX field:', error);

          // Check if it's a specific ES error we can handle
          if (error instanceof Error && error.message.includes('timeout')) {
            console.warn(
              'Elasticsearch query timed out, returning static results only',
            );
          }

          return [];
        }

      default:
        return [];
    }
  },
  searchAgreementUuidsForMyDocuments: async (
    currentPage: 'search' | 'dashboard',
    ignorePagination?: boolean,
  ) => {
    // Build a combined ES query based on filtersStore.filterByIds (AND),
    const { searchPageResults } = get();
    // exclude archived, apply single-column sorting, and paginate by filtersStore.pagination
    const { getFilterByIds, pagination } = useFiltersStore.getState();
    if (
      currentPage === 'search' &&
      getFilterByIds(currentPage) &&
      Object.keys(getFilterByIds(currentPage)).length === 0
    ) {
      return [];
    }
    const { sorting } = useFileExplorerStore.getState();
    const { steps, indexes, users } = useGlobalStore.getState();
    const { directories, insuranceObjects } = useApiManagerStore.getState();

    const must: any[] = [existsEntity('public_agreements_uuid')];
    const mustNot: any[] = [];

    // Exclude archived by step/status name "В Архів"
    const archiveStepUuids = steps
      .filter((s) => s.status_name?.trim().toLowerCase() === 'в архів')
      .map((s) => s.uuid);

    if (currentPage === 'dashboard' && archiveStepUuids.length > 0) {
      mustNot.push({
        bool: {
          should: archiveStepUuids.map((uuid) =>
            termEither('public_agreements_step_id', uuid),
          ),
          minimum_should_match: 1,
        },
      });
    }

    const isIdField = (field: string) =>
      field === 'assignee' ||
      field === 'creator' ||
      field === 'step' ||
      field === 'index' ||
      field === 'insurance_object';

    const isDateField = (field: string) =>
      field === 'created' || field === 'modified';

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    const mapIdFieldValueToUuid = (
      field: string,
      rawValue: unknown,
    ): string | null => {
      const value = (rawValue ?? '').toString().trim();
      if (!value) return null;
      if (uuidRegex.test(value)) return value;

      switch (field) {
        case 'index_id': {
          const found = indexes
            .filter((i) => !i.is_deleted)
            .find((i) => i.name.toLowerCase() === value.toLowerCase());
          return found?.uuid ?? null;
        }
        case 'step_id': {
          const found = steps
            .filter((s) => !s.is_deleted)
            .find(
              (s) =>
                (s.status_name || '').toLowerCase() === value.toLowerCase(),
            );
          return found?.uuid ?? null;
        }
        case 'assignee_id':
        case 'creator_id': {
          const found = users?.find((u) => {
            const fn = (u.first_name || '').toLowerCase();
            const ln = (u.last_name || '').toLowerCase();
            const full = `${fn} ${ln}`.trim();
            const target = value.toLowerCase();
            return full === target || fn === target || ln === target;
          });

          return found?.uuid ?? null;
        }
        case 'insurance_object_id': {
          const insuranceObject = insuranceObjects.find(
            (i) => i.name.toLowerCase() === value.toLowerCase(),
          );
          const directory = directories.find(
            (i) => i.uuid === insuranceObject?.directory,
          );
          return directory?.uuid ?? null;
        }
        default:
          return null;
      }
    };

    // Batch resolve multiple user names to UUIDs via one ES query
    const resolveMultipleUserNamesToUuids = async (
      names: string[],
    ): Promise<string[]> => {
      const unique = Array.from(
        new Set(
          names
            .map((n) => (n ?? '').toString().trim().toLowerCase())
            .filter((n) => n.length > 0),
        ),
      );
      if (unique.length === 0) return [];
      try {
        const shouldPerName = unique.map((raw) => ({
          bool: {
            should: [
              {
                wildcard: {
                  public_users_first_name: {
                    value: `*${raw}*`,
                    case_insensitive: true,
                  },
                },
              },
              {
                wildcard: {
                  public_users_last_name: {
                    value: `*${raw}*`,
                    case_insensitive: true,
                  },
                },
              },
              {
                wildcard: {
                  public_users_email: {
                    value: `*${raw}*`,
                    case_insensitive: true,
                  },
                },
              },
            ],
            minimum_should_match: 1,
          },
        }));

        const userQuery = {
          query: {
            bool: {
              must: [
                { term: { table: 'users' } },
                {
                  bool: {
                    should: shouldPerName,
                    minimum_should_match: 1,
                  },
                },
              ],
            },
          },
          size: Math.min(1000, unique.length * 50),
          _source: ['public_users_uuid'],
          timeout: '30s',
        } as const;
        const res = await apiService.elasticSearch(userQuery);
        return [
          ...new Set(res.hits.hits.map((h) => h._source.public_users_uuid)),
        ];
      } catch {
        return [];
      }
    };

    // Collect any user-based filters to resolve (arrays supported)
    const userFieldToResolvedUuids: Record<string, string[]> = {};
    if (Object.keys(getFilterByIds(currentPage)).length > 0) {
      for (const [field, { filterByValues }] of Object.entries(
        getFilterByIds(currentPage),
      )) {
        if (field === 'assignee_id' || field === 'creator_id') {
          const resolved = new Set<string>();
          const uuidsDirect: string[] = [];
          const namesToResolve: string[] = [];

          for (const v of filterByValues) {
            const raw = (v.value ?? '').toString().trim();
            if (!raw) continue;
            if (uuidRegex.test(raw)) {
              uuidsDirect.push(raw);
            } else {
              namesToResolve.push(raw);
            }
          }

          uuidsDirect.forEach((id) => resolved.add(id));

          if (namesToResolve.length > 0 && users && users.length > 0) {
            const loweredTargets = Array.from(
              new Set(namesToResolve.map((n) => n.toLowerCase())),
            );
            for (const u of users) {
              const fn = (u.first_name || '').toLowerCase();
              const ln = (u.last_name || '').toLowerCase();
              const full = `${fn} ${ln}`.trim();
              for (const target of loweredTargets) {
                if (
                  full === target ||
                  fn === target ||
                  ln === target ||
                  fn.includes(target) ||
                  ln.includes(target)
                ) {
                  resolved.add(u.uuid);
                }
              }
            }
          }

          const unresolved = Array.from(
            new Set(namesToResolve.map((n) => n.toLowerCase())),
          ).filter((n) => !Array.from(resolved).some((id) => id === n));

          if (unresolved.length > 0) {
            const viaEs = await resolveMultipleUserNamesToUuids(unresolved);
            viaEs.forEach((id) => resolved.add(id));
          }

          userFieldToResolvedUuids[field] = Array.from(resolved);
        }
      }
    }

    // If a user filter exists but resolved to zero UUIDs, return [] (AND semantics)
    if (
      Object.keys(userFieldToResolvedUuids).some((k) => {
        const selected = getFilterByIds(currentPage)?.[k]?.filterByValues || [];
        return Array.isArray(selected) && selected.length > 0
          ? userFieldToResolvedUuids[k].length === 0
          : false;
      })
    ) {
      return [];
    }

    // AND all active filters
    if (
      getFilterByIds(currentPage) &&
      Object.keys(getFilterByIds(currentPage)).length > 0
    ) {
      for (const [field, { filterByValues }] of Object.entries(
        getFilterByIds(currentPage),
      )) {
        const esField = mapSortIdToField(field);

        if (filterByValues.length === 0) continue;

        if (isIdField(field)) {
          const mappedUuids = Array.from(
            new Set(
              filterByValues
                .map((v) => mapIdFieldValueToUuid(field, v.value))
                .filter((x): x is string => Boolean(x)),
            ),
          );
          if (mappedUuids.length === 0) {
            return [];
          }

          must.push({
            bool: {
              should: mappedUuids.map((id) => ({
                term: { [esField]: id },
              })),
              minimum_should_match: 1,
            },
          });
          continue;
        }

        if (isDateField(field)) {
          const fromRaw = filterByValues[0]?.value;
          const toRaw = filterByValues[1]?.value;
          if (!fromRaw || !toRaw) {
            return [];
          }

          const fromMoment = moment(fromRaw as string | Date).startOf('day');
          const toMoment = moment(toRaw as string | Date).endOf('day');

          if (!fromMoment.isValid() || !toMoment.isValid()) {
            return [];
          }

          const start = fromMoment.isBefore(toMoment) ? fromMoment : toMoment;
          const end = fromMoment.isBefore(toMoment) ? toMoment : fromMoment;

          // Use explicit UTC instants with offset format (e.g., +00:00) to match stored values
          const gteIso = start.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
          const lteIso = end.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');

          must.push({ range: { [esField]: { gte: gteIso, lte: lteIso } } });
          continue;
        }

        // String fields: case-insensitive contains via wildcard (OR within field)
        const normalized = Array.from(
          new Set(
            filterByValues
              .map((v) => (v.value ?? '').toString().trim())
              .filter((s) => s.length > 0),
          ),
        );
        if (normalized.length === 0) {
          return [];
        }

        must.push({
          bool: {
            should: normalized.map((raw) => ({
              wildcard: {
                [esField]: {
                  value: `*${raw}*`,
                  case_insensitive: true,
                },
              },
            })),
            minimum_should_match: 1,
          },
        });
      }
    }

    // Sorting: single active sort
    const sortClauses: any[] = [];
    if (sorting && sorting.length > 0) {
      const active = sorting[0];
      const uuid = active.id; // e.g.,  'registration_plate', 'created', 'modified', etc.

      switch (uuid) {
        case 'insurance_object':
          // Sort by directory abbreviation derived from insurance_object → directory
          // Fetch mapping via Elasticsearch to ensure completeness
          const dirMap = new Map<string, string>();
          for (const dir of directories) {
            dirMap.set(dir.uuid, (dir.abbreviation || '').toLowerCase());
          }
          const idToAbbr: Record<string, string> = {};
          try {
            const esIoResp = await apiService.elasticSearch({
              query: {
                bool: {
                  must: [
                    {
                      term: { table: 'insurance_objects' },
                    },
                  ],
                },
              },
              size: 1000,
              _source: [
                'public_insurance_objects_uuid',
                'public_insurance_objects_directory_id',
              ],
              timeout: '30s',
            });

            for (const hit of esIoResp.hits.hits) {
              const ioId = hit._source.public_insurance_objects_uuid as string;
              const dirId = hit._source
                .public_insurance_objects_directory_id as string;
              const abbr = dirMap.get(dirId) || '';
              if (ioId && abbr) idToAbbr[ioId] = abbr;
            }
          } catch {
            // If ES lookup fails, fall back to whatever is in the store (best-effort)
            for (const io of insuranceObjects) {
              const abbr = dirMap.get(io.directory) || '';
              if (abbr) idToAbbr[io.uuid] = abbr;
            }
          }
          const allAbbrs = Array.from(new Set(Object.values(idToAbbr))).sort();
          const abbrToRank: Record<string, number> = {};
          allAbbrs.forEach((abbr, idx) => (abbrToRank[abbr] = idx + 1));
          const idToRank: Record<string, number> = {};
          for (const [id, abbr] of Object.entries(idToAbbr)) {
            const rank = abbrToRank[abbr];
            if (typeof rank === 'number') idToRank[id] = rank;
          }

          const order = 'asc';

          sortClauses.push({
            _script: {
              type: 'string',
              order,
              script: {
                lang: 'painless',
                source: `
                  def has = doc.containsKey('public_agreements_insurance_object_id.enum') &&
                             !doc['public_agreements_insurance_object_id.enum'].empty;
                  if (!has) return params.missing;
                  def key = doc['public_agreements_insurance_object_id.enum'].value;
                  def rank = params.idToRank.get(key);
                  if (rank == null) return params.missing;
                  int target = params.desc ? (params.maxRank - rank) : rank;
                  def s = target.toString();
                  int pad = 6 - s.length();
                  if (pad > 0) { s = '000000'.substring(0, pad) + s; }
                  def nm = params.idToAbbr.get(key);
                  if (nm == null) nm = '';
                  return s + '|' + nm + '|' + key;
              `,
                params: {
                  idToRank: idToRank,
                  idToAbbr: idToAbbr,
                  maxRank: allAbbrs.length + 10,
                  desc: active.desc,
                  missing: '999999|zzzzzz|zzzzzz',
                },
              },
            },
          });
          break;

        case 'index':
          // Strict sort: numeric rank per index name, stable tie-breakers
          const idToName: Record<string, string> = {};
          for (const idx of indexes) {
            if (!idx.is_deleted) {
              idToName[idx.uuid] = (idx.name || '').toLowerCase();
            }
          }
          const uniqueNames = Array.from(
            new Set(Object.values(idToName).filter((v) => v && v.length > 0)),
          ).sort();
          const nameToRank: Record<string, number> = {};
          uniqueNames.forEach((n, i) => (nameToRank[n] = i + 1));
          const idToRankStrict: Record<string, number> = {};
          for (const [id, nm] of Object.entries(idToName)) {
            const r = nameToRank[nm];
            if (typeof r === 'number') idToRankStrict[id] = r;
          }
          sortClauses.push({
            _script: {
              type: 'string',
              order: 'asc',
              script: {
                lang: 'painless',
                source: `
                  def has = doc.containsKey('public_agreements_index_id.enum') &&
                             !doc['public_agreements_index_id.enum'].empty;
                  if (!has) return params.missing;
                  def key = doc['public_agreements_index_id.enum'].value;
                  def rank = params.idToRank.get(key);
                  if (rank == null) return params.missing;
                  int target = params.desc ? (params.maxRank - rank) : rank;
                  def s = target.toString();
                  int pad = 6 - s.length();
                  if (pad > 0) { s = '000000'.substring(0, pad) + s; }
                  def nm = params.idToName.get(key);
                  if (nm == null) nm = '';
                  return s + '|' + nm + '|' + key;
                `,
                params: {
                  idToRank: idToRankStrict,
                  idToName: idToName,
                  maxRank: uniqueNames.length + 10,
                  desc: active.desc,
                  missing: '999999|zzzzzz|zzzzzz',
                },
              },
            },
          });
          break;

        case 'name':
          // Locale-agnostic (Cyrillic-friendly) sort by lower-cased name with UUID tie-breaker
          sortClauses.push({
            _script: {
              type: 'string',
              order: active.desc ? 'desc' : 'asc',
              script: {
                lang: 'painless',
                source: `
                  String raw = '';
                  if (doc.containsKey('public_agreements_name.enum') && !doc['public_agreements_name.enum'].empty) {
                    raw = doc['public_agreements_name.enum'].value;
                  } else if (doc.containsKey('public_agreements_name') && !doc['public_agreements_name'].empty) {
                    raw = doc['public_agreements_name'].value;
                  }
                  if (raw == null) raw = '';
                  raw = raw.trim()
                           .replace('\u2011','-').replace('\u2013','-').replace('\u2014','-').replace('\u2212','-');
                  String s = raw.toLowerCase();
                  if (s.length() == 0) return params.missing;

                  // Natural sort: zero-pad digit runs so "0000-13" < "0000-133"
                  StringBuilder sb = new StringBuilder();
                  StringBuilder digits = new StringBuilder();
                  for (int i = 0; i < s.length(); i++) {
                    char c = s.charAt(i);
                    if (Character.isDigit(c)) {
                      digits.append(c);
                    } else {
                      if (digits.length() > 0) {
                        try {
                          String num = Long.toString(Long.parseLong(digits.toString()));
                          int padLen = 12 - num.length();
                          while (padLen-- > 0) sb.append('0');
                          sb.append(num);
                        } catch (Exception e) {
                          sb.append(digits.toString());
                        }
                        digits.setLength(0);
                      }
                      sb.append(c);
                    }
                  }
                  if (digits.length() > 0) {
                    try {
                      String num2 = Long.toString(Long.parseLong(digits.toString()));
                      int padLen2 = 12 - num2.length();
                      while (padLen2-- > 0) sb.append('0');
                      sb.append(num2);
                    } catch (Exception e2) {
                      sb.append(digits.toString());
                    }
                    digits.setLength(0);
                  }

                  String id = '';
                  if (doc.containsKey('public_agreements_uuid.enum') && !doc['public_agreements_uuid.enum'].empty) {
                    id = doc['public_agreements_uuid.enum'].value;
                  }
                  char first = s.charAt(0);
                  String lead = Character.isDigit(first) ? '0' : '1';
                  return lead + '|' + sb.toString() + '|' + id;
                `,
                params: {
                  missing: active.desc ? '' : '2||',
                },
              },
            },
          });
          break;

        default:
          const mappedField = mapSortIdToField(uuid);
          const fieldForSort = mappedField;
          sortClauses.push({
            [fieldForSort]: {
              order: active.desc ? 'desc' : 'asc',
            },
          });
          break;
      }
    }

    const size = ignorePagination
      ? 1000
      : Math.min(100, pagination?.pageSize ?? 20);
    const from = ignorePagination ? 0 : (pagination?.pageIndex ?? 0) * size;

    const { user } = useAuthStore.getState();
    if (user?.role === USER_ROLES.EXECUTOR.value) {
      must.push({
        bool: {
          should: [
            termEither('public_agreements_creator_id', user.uuid),
            termEither('public_agreements_assignee_id', user.uuid),
          ],
          minimum_should_match: 1,
        },
      });
    }

    try {
      const payload = {
        query: {
          bool: {
            must,
            ...(mustNot.length > 0 ? { must_not: mustNot } : {}),
          },
        },
        from,
        size,
        _source: ['public_agreements_uuid'],
        timeout: '30s',
        ...(sortClauses.length > 0 ? { sort: sortClauses } : {}),
      } as const;

      const elasticSearch = await apiService.elasticSearch(payload);
      const result = [
        ...new Set(
          elasticSearch.hits.hits.map((i) => i._source.public_agreements_uuid),
        ),
      ];

      return currentPage === 'search'
        ? result.filter(
            (i) =>
              !searchPageResults.length || searchPageResults.includes(i),
          )
        : result;
    } catch (e) {
      console.error('Elasticsearch combined filter query failed:', e);
      return [];
    }
  },
  searchAgreementFields: async (
    currentPage: 'search' | 'dashboard',
    field: string,
  ) => {
    const isDateField = (field: string) =>
      field === 'created' || field === 'modified';
    const { searchPageResults } = get();
    const { getFilterByIds } = useFiltersStore.getState();
    const { steps, indexes } = useGlobalStore.getState();

    const must: any[] = [existsEntity('public_agreements_uuid')];
    const mustNot: any[] = [];
    const should: any[] = [];

    // Exclude archived by step/status name "В Архів"
    const archiveStepUuids = steps
      .filter((s) => s.status_name?.trim().toLowerCase() === 'в архів')
      .map((s) => s.uuid);
    if (currentPage === 'dashboard' && archiveStepUuids.length > 0) {
      mustNot.push({
        bool: {
          should: archiveStepUuids.map((uuid) =>
            termEither('public_agreements_step_id', uuid),
          ),
          minimum_should_match: 1,
        },
      });
    }

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    if (Object.keys(getFilterByIds(currentPage)).length > 0) {
      for (const [field, { filterByValues }] of Object.entries(
        getFilterByIds(currentPage),
      )) {
        if (filterByValues.length === 0) continue;

        if (isDateField(field)) {
          const fromRaw = filterByValues[0]?.value;
          const toRaw = filterByValues[1]?.value;
          if (!fromRaw || !toRaw) {
            return [];
          }

          const fromMoment = moment(fromRaw as string | Date).startOf('day');
          const toMoment = moment(toRaw as string | Date).endOf('day');

          if (!fromMoment.isValid() || !toMoment.isValid()) {
            return [];
          }

          const start = fromMoment.isBefore(toMoment) ? fromMoment : toMoment;
          const end = fromMoment.isBefore(toMoment) ? toMoment : fromMoment;

          // Use explicit UTC instants with offset format (e.g., +00:00) to match stored values
          const gteIso = start.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
          const lteIso = end.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');

          must.push({
            range: { [mapSortIdToField(field)]: { gte: gteIso, lte: lteIso } },
          });
          continue;
        }

        if (field === 'index') {
          const indexUuids = new Set<string>();

          for (const { value } of filterByValues) {
            const raw = (value ?? '').toString().trim();
            if (!raw) continue;

            if (uuidRegex.test(raw)) {
              indexUuids.add(raw);
              continue;
            }

            const matchedIndex = indexes.find(
              (idx) =>
                !idx.is_deleted &&
                (idx.name || '').toLowerCase() === raw.toLowerCase(),
            );

            if (matchedIndex?.uuid) {
              indexUuids.add(matchedIndex.uuid);
            }
          }

          if (indexUuids.size === 0) {
            return [];
          }

          must.push(
            termsEither(mapSortIdToField(field), Array.from(indexUuids)),
          );
          continue;
        }

        must.push(
          termsEither(
            mapSortIdToField(field),
            filterByValues.map((fv) => fv.value as string),
          ),
        );
      }
    }

    const mapFieldToEs = (id: string): string => {
      switch (id) {
        case 'index':
          return 'public_agreements_index_id';
        case 'step':
          return 'public_agreements_step_id';
        case 'assignee':
          return 'public_agreements_assignee_id';
        case 'creator':
          return 'public_agreements_creator_id';
        case 'created':
          return 'public_agreements_created';
        case 'modified':
          return 'public_agreements_modified';
        case 'insurance_object':
          return 'public_agreements_insurance_object_id';
        default:
          return `public_agreements_${id}`;
      }
    };

    const esField = mapFieldToEs(field);

    const { user } = useAuthStore.getState();
    if (user?.role === USER_ROLES.EXECUTOR.value) {
      must.push({
        bool: {
          should: [
            termEither('public_agreements_creator_id', user.uuid),
            termEither('public_agreements_assignee_id', user.uuid),
          ],
          minimum_should_match: 1,
        },
      });
    }
    try {
      const { totalActiveAgreements } = useApiManagerStore.getState();
      const payload = {
        query: {
          bool: {
            must,
            ...(mustNot.length > 0 ? { must_not: mustNot } : {}),
            ...(should.length > 0 ? { should } : {}),
          },
        },
        _source: ['public_agreements_uuid', esField],
        size: Math.max(totalActiveAgreements, 1000),
        timeout: '30s',
      } as const;

      const elasticSearch = await apiService.elasticSearch(payload);
      const filtered =
        currentPage === 'search'
          ? elasticSearch.hits.hits.filter(
              (i) =>
                !searchPageResults.length ||
                searchPageResults.includes(
                  i._source.public_agreements_uuid,
                ),
            )
          : elasticSearch.hits.hits;
      const result = filtered.map((i) => ({
        uuid: i._source.public_agreements_uuid,
        value: i._source[esField],
      }));

      return [...new Set(result.filter((i) => !!i.value).map((i) => i.value))];
    } catch (e) {
      console.error('Elasticsearch combined filter query failed:', e);
      return [];
    }
  },
  getDirectoriesByInsuranceObjects: async (
    insuranceObjects: string[],
  ): Promise<{ uuid: string; directory: string }[]> => {
    const must: any[] = [
      {
        term: {
          table: 'insurance_objects',
        },
      },
      {
        terms: { 'public_insurance_objects_uuid.enum': insuranceObjects },
      },
    ];
    try {
      const payload = {
        query: {
          bool: {
            must,
          },
        },
        size: Math.min(Math.max(insuranceObjects.length, 1), 10000),
        _source: [
          'public_insurance_objects_directory_id',
          'public_insurance_objects_uuid',
        ],
        timeout: '30s',
      } as const;

      const elasticSearch = await apiService.elasticSearch(payload);
      const result = elasticSearch.hits.hits.map((i) => ({
        uuid: i._source.public_insurance_objects_uuid,
        directory: i._source.public_insurance_objects_directory_id,
      }));
      return result;
    } catch (e) {
      console.error('Elasticsearch combined filter query failed:', e);
      return [];
    }
  },
  getAgreementsByMask: async (mask: string) => {
    const { indexes } = useGlobalStore.getState();

    const tokens = mask
      .split('_')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const tokensLower = tokens.map((s) => s.toLowerCase());
    const possibleIndex = indexes.filter(
      (i) => !i.is_deleted && tokensLower.includes(i.name.toLowerCase()),
    );
    const possibleIndexUuids = possibleIndex.map((i) => i.uuid);
    const indexNamesLower = indexes
      .filter((i) => !i.is_deleted)
      .map((i) => i.name.toLowerCase());
    const tokensForFields = tokensLower.filter(
      (t) => !indexNamesLower.includes(t),
    );

    const must: any[] = [
      {
        term: {
          table: 'agreements',
        },
      },
    ];
    const should: any[] = [];
    // Boost agreements that match the index id
    if (possibleIndexUuids.length > 0) {
      should.push({
        terms: {
          'public_agreements_index_id.enum': possibleIndexUuids,
          boost: 5,
        },
      });
    }
    // Add token-based matching over key fields with boosts
    if (tokensForFields.length > 0) {
      for (const v of tokensForFields) {
        should.push({
          wildcard: {
            'public_agreements_registration_plate.enum': {
              value: `*${v}*`,
              case_insensitive: true,
              boost: 4,
            },
          },
        });
        should.push({
          wildcard: {
            'public_agreements_name.enum': {
              value: `*${v}*`,
              case_insensitive: true,
              boost: 2,
            },
          },
        });
      }
    }

    const { user } = useAuthStore.getState();
    if (user?.role === USER_ROLES.EXECUTOR.value) {
      must.push({
        bool: {
          should: [
            { term: { 'public_agreements_creator_id.enum': user.uuid } },
            { term: { 'public_agreements_assignee_id.enum': user.uuid } },
          ],
          minimum_should_match: 1,
        },
      });
    }

    try {
      const payload = {
        query: {
          bool: {
            must,
            ...(should.length > 0 ? { should, minimum_should_match: 1 } : {}),
          },
        },
        size: 20,
        _source: [
          'public_agreements_uuid',
          'public_agreements_name',
          'public_agreements_index_id',
          'public_agreements_registration_plate',
        ],
        timeout: '30s',
        sort: [{ _score: 'desc' }],
      } as const;

      const elasticSearch = await apiService.elasticSearch(payload);
      const result = elasticSearch.hits.hits.map((hit) => ({
        label:
          `${indexes.filter((i) => !i.is_deleted).find((i) => i.uuid === hit._source.public_agreements_index_id)?.name}_${hit._source.public_agreements_registration_plate}_${hit._source.public_agreements_name}`.toUpperCase(),
        value: hit._source.public_agreements_uuid,
      }));
      return result;
    } catch (e) {
      console.error('Elasticsearch combined filter query failed:', e);
      return [];
    }
  },
  searchUsersByMask: async (mask: string) => {
    const tokens = mask
      .split(' ')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const tokensLower = tokens.map((s) => s.toLowerCase());

    const must: any[] = [
      {
        term: {
          table: 'users',
        },
      },
    ];

    const should: any[] = [
      termEither('public_users_user_type', USER_ROLES.EXECUTOR.value),
      termEither('public_users_user_type', USER_ROLES.CONTROLLER.value),
    ];

    // Add token-based matching over key fields with boosts
    if (tokensLower.length > 0) {
      for (const v of tokensLower) {
        should.push({
          wildcard: {
            'public_users_first_name.enum': {
              value: `*${v}*`,
              case_insensitive: true,
              boost: 4,
            },
          },
        });
        should.push({
          wildcard: {
            'public_users_last_name.enum': {
              value: `*${v}*`,
              case_insensitive: true,
              boost: 2,
            },
          },
        });
      }
    }

    try {
      const payload = {
        query: {
          bool: {
            must,
            ...(should.length > 0 ? { should, minimum_should_match: 1 } : {}),
          },
        },
        size: 50,
        _source: [
          'public_users_first_name',
          'public_users_last_name',
          'public_users_uuid',
        ],
        timeout: '30s',
        sort: [{ _score: 'desc' }],
      } as const;

      const elasticSearch = await apiService.elasticSearch(payload);
      const result = elasticSearch.hits.hits.map((hit) => ({
        value: hit._source.public_users_uuid,
        label: `${hit._source.public_users_first_name} ${hit._source.public_users_last_name}`,
      }));
      return result;
    } catch (e) {
      console.error('Elasticsearch combined filter query failed:', e);
      return [];
    }
  },
}));
